/**
 * Pure flow selectors — the resume/recapture logic, pinned by tests without React.
 */

import type { CaptureState, ScriptShot } from '@/api/capture';

/** The shots this visit must produce: the whole script, or only the recapture-requested prompts. */
export function activeShots(script: readonly ScriptShot[], state: CaptureState | null): ScriptShot[] {
  const only = state?.recaptureOnly;
  // A recapture visit keeps its CHALLENGE shot(s): the be re-issues one in the subset script and
  // refuses submission without a sealed challenge (SV_CHALLENGE_UNANSWERED).
  const list = only && only.length > 0 ? script.filter((s) => only.includes(s.promptKey) || s.promptKey === 'CHALLENGE' || s.challenge !== undefined) : [...script];
  return list.sort((a, b) => a.ordinal - b.ordinal);
}

/** Shot ids the server (or this visit) already holds. */
export function completedIds(state: CaptureState | null, local: readonly string[]): Set<string> {
  return new Set([...(state?.completedShots ?? []), ...local]);
}

export function remainingShots(active: readonly ScriptShot[], done: ReadonlySet<string>): ScriptShot[] {
  return active.filter((s) => !done.has(s.shotId));
}

/** Index (in `active`) of the first shot still to capture; `active.length` when none remain. */
export function firstRemainingIndex(active: readonly ScriptShot[], done: ReadonlySet<string>): number {
  const i = active.findIndex((s) => !done.has(s.shotId));
  return i === -1 ? active.length : i;
}

const INTERIOR = /^(INT_|CERT_WALL|ACTIVITY|INSTALL_POINT|HOME_INT)/;

/** True for prompts that record inside the premises — the bystander-minimisation interstitial precedes the first of these. */
export function isInteriorPrompt(promptKey: string): boolean {
  return INTERIOR.test(promptKey);
}

export function isSelfiePrompt(promptKey: string): boolean {
  return promptKey === 'REP_SELFIE';
}

/** The identity document still: `ID_DOCUMENT` (contract v0.1.9 §A — every template, after REP_SELFIE, mandatory)
 *  and the pre-v0.1.9 `REP_ID` an older be may still issue. Both get the document guidance + the stricter floor. */
export function isIdDocPrompt(promptKey: string): boolean {
  return promptKey === 'ID_DOCUMENT' || promptKey === 'REP_ID';
}

/** Which lens a prompt wants by default: the representative's own face → front; everything else → rear. */
export function defaultFacing(promptKey: string): 'user' | 'environment' {
  return isSelfiePrompt(promptKey) ? 'user' : 'environment';
}
